/**
 * Research AI-chain — per-company MANUAL-trigger contract (scrape-first + Haiku).
 *
 * Design: decision-log.md § 2026-08-03 (THREE accepted ADRs — scrape-first+Haiku;
 * AI-chain manual-only, per-company/checkbox; Company Data Provider) + the plan
 * docs/proposals/research-ai-chain-scrape-first.md §6.
 *
 * The paid AI-chain is DECOUPLED from discovery: after discovery/dedup a row sits at
 * `aiStatus='idle'` and NOTHING runs. The chain fires ONLY from an explicit UI action
 * — one row, or a checkbox-selected set — NEVER over the whole discovered list
 * (no-automatic-AI policy: decision-log 2026-07-20 + the 2026-08-03 manual-only
 * extension). Each row's run walks a SEPARATE machine `aiRunStatus`
 * (idle → gathering → extracting → verifying → scored | failed), distinct from the
 * discovery-side `stage` (research-pipeline.ts). `failed` is terminal until a fresh
 * explicit click (attempts=1, no auto-retry / no failover).
 *
 * Phase A scope (§8): the enum + trigger request/response shapes + the batch cap
 * ONLY. The driver (processCompanyAiChain), the scrape-first + Haiku stage adapters,
 * and the routes are later phases (B–F). This file only imports `zod`, so the barrel
 * star re-export is self-contained (no transitive dependency).
 *
 * Feature-owned; re-exported from `@eer/shared` (packages/shared/src/index.ts) by the
 * serial Wire pass — it MUST NOT edit the shared barrel during parallel work.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Per-company AI-run status machine (research_companies.ai_run_status)
// ---------------------------------------------------------------------------
/**
 * Per-company AI-run status. SEPARATE from the discovery-side `stage` machine
 * (research-pipeline.ts): `stage` walks discovered→deduped (free, auto); `aiRunStatus`
 * walks the paid chain, only on a user click. `failed` is terminal until a new
 * explicit click (attempts=1, no auto-retry — no-automatic-AI policy).
 */
export const AI_RUN_STATUSES = [
    'idle',
    'gathering',
    'extracting',
    'verifying',
    'scored',
    'failed',
];
export const aiRunStatusSchema = z.enum(AI_RUN_STATUSES);
/** Non-terminal AI-run statuses — a row here is mid-flight (UI polls for progress). */
export const ACTIVE_AI_RUN_STATUSES = ['gathering', 'extracting', 'verifying'];
/** True while a row's AI-run is mid-flight (not idle / scored / failed). */
export function isActiveAiRunStatus(status) {
    return ACTIVE_AI_RUN_STATUSES.includes(status);
}
// ---------------------------------------------------------------------------
// Manual triggers (per-row + checkbox batch). userInitiated is server-derived.
// ---------------------------------------------------------------------------
/**
 * BACKSTOP ceiling for a single bulk "Run AI" click (open-decision #5, resolved by
 * PM 2026-08-17: "не жёсткая константа 25 … backstop-потолок в коде — 50").
 *
 * It is a typo guard, NOT the product decision: the user types the batch volume in
 * the UI (pre-filled with {@link AI_RUN_BATCH_DEFAULT}), and only an input above this
 * ceiling is refused with "narrow the selection". Each selected row is a SEPARATE
 * paid Haiku chain, so the UI still confirms the exact count before sending.
 */
export const AI_RUN_BATCH_CAP = 50;
/**
 * Pre-filled value of the batch-volume field in the UI (open-decision #5). The user
 * may raise it up to {@link AI_RUN_BATCH_CAP} or lower it; nothing is ever run
 * without an explicit click + confirmation (no-automatic-AI, 2026-07-20).
 */
export const AI_RUN_BATCH_DEFAULT = 10;
/**
 * POST /research/:requestId/companies/:rowId/run-ai — trigger ONE row.
 * The actor + `userInitiated` are taken server-side from the auth context (never from
 * the client body), so no background path can forge a paid run. Written to
 * research_companies.ai_run_started_by.
 */
export const aiRunOneResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    rowId: z.number(),
    aiStatus: aiRunStatusSchema, // usually 'gathering' right after enqueue
});
/**
 * POST /research/:requestId/companies/run-ai — trigger a checkbox-selected set.
 * Capped at AI_RUN_BATCH_CAP; the UI confirms the paid-run count before sending.
 */
export const aiRunBatchRequestSchema = z.object({
    rowIds: z.array(z.number().int().positive()).min(1).max(AI_RUN_BATCH_CAP),
});
export const aiRunBatchResponseSchema = z.object({
    success: z.literal(true).optional(),
    /** Rows accepted + enqueued, with their new (usually 'gathering') status. */
    enqueued: z.array(z.object({ rowId: z.number(), aiStatus: aiRunStatusSchema })),
    /** Rows skipped (e.g. already mid-flight) with a reason for the UI. */
    skipped: z.array(z.object({ rowId: z.number(), reason: z.string() })),
});
/**
 * E12-F17-T06 — GET /research/:requestId/companies/run-ai/quote. Read-only (no AI call, no
 * write): which verdict path the AI check runs on right now and the estimated price of ONE
 * company's chain on it, so the confirmation can name the chain and price the selection.
 *   - 'jev'   → free site collection + ONE Jev verdict call + ONE Haiku firmographics call;
 *   - 'haiku' → free site collection + Haiku link selection, extraction, verification and
 *               the grade explanation (the rollback path).
 * The engine enum is spelled out here (same values as `jevVerdictEngineSchema`) so this file
 * keeps importing only `zod`.
 */
export const aiCheckQuoteResponseSchema = z.object({
    success: z.literal(true).optional(),
    verdictEngine: z.enum(['jev', 'haiku']),
    /** Estimated USD of ONE company's chain on that path (average of recent priced calls). */
    costPerCompanyUsd: z.number().nonnegative(),
    /** True when some call of the path had no priced history and a conservative constant is used. */
    priceIsFallbackEstimate: z.boolean(),
});
// ---------------------------------------------------------------------------
// E06-F03-T13-S03 — a row with no website is never started
// ---------------------------------------------------------------------------
/**
 * The UI label for a row whose website is unknown. It is "not found" and never
 * "no website": we searched and could not find it, which does not mean the company
 * has none. The user can enter it in Review.
 */
export const WEBSITE_NOT_FOUND_LABEL = 'Website not found';
/** `skipped[].reason` for a row that has no website: it was not started. */
export const AI_RUN_SKIP_NO_WEBSITE = 'no-website';
/** What the user reads when the AI check is refused because the website is unknown. */
export const AI_RUN_NO_WEBSITE_MESSAGE = 'Website not found — enter the company website in Review before running the AI check.';
// ---------------------------------------------------------------------------
// E12-F06-T37 — a row headquartered in an excluded state is never started
// ---------------------------------------------------------------------------
/**
 * `skipped[].reason` for a row whose HQ state (as discovery stored it) is on the
 * request's Excluding locations (Do-Not-Source States): the paid AI check is not run.
 * Same value as the discovery cut reason, so it is counted in "Excluded at search".
 */
export const AI_RUN_SKIP_EXCLUDED_STATE = 'excluded_state';
/** What the user reads when the AI check is refused for an excluded HQ state. */
export const AI_RUN_EXCLUDED_STATE_MESSAGE = 'HQ is in an excluded location (Excluding locations / Do-Not-Source States) — the AI check is not run for this company.';
// ---------------------------------------------------------------------------
// Collected-source provenance (what the model actually read) — E06-F05-T05
// ---------------------------------------------------------------------------
/**
 * ONE page of the collected `site_text` blob, exactly as it was stored BEFORE any
 * model touched it (`company_data_cache`, provider='scrape', kind='site_text' —
 * open-decision #1). `text` is the visible-text extraction the scraper persisted:
 * the same string the Haiku prompts were fed, so the panel is a faithful "what did
 * the AI read" view, not a re-render.
 */
export const collectedPageSchema = z.object({
    url: z.string(),
    /** 'home' = the entry page, 'selected' = a link the link-select stage picked. */
    role: z.enum(['home', 'selected']),
    /** The visible text of the page (HTML/CSS/JS stripped before storage). */
    text: z.string(),
    /** Size of `text` in bytes at collection time. */
    bytes: z.number(),
    /** True when the per-page cap truncated the text. */
    truncated: z.boolean(),
    /** Transport that fetched it: plain fetch or the Playwright fallback. */
    fetchedVia: z.enum(['fetch', 'playwright']),
});
/**
 * GET /research/:requestId/companies/:rowId/sources — the provenance panel payload.
 *
 * `collected:false` (empty `pages`) is a NORMAL answer, not an error: nothing has been
 * gathered for this company yet (no run, or the run failed before gathering). The UI
 * renders an explanatory empty state instead of pretending the model saw nothing.
 */
export const collectedSourcesResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    rowId: z.number(),
    /** Domain the scrape ran against (null when the row has no website at all). */
    domain: z.string().nullable(),
    /** Entry URL of the scrape (null when nothing was collected). */
    mainUrl: z.string().nullable(),
    /** True when there IS stored site text for this company. */
    collected: z.boolean(),
    /** When the pages were downloaded (ISO) — null when nothing was collected. */
    collectedAt: z.string().nullable(),
    /** True while the stored text is inside the 90-day reuse window. */
    fresh: z.boolean(),
    /** Total bytes across all collected pages. */
    totalBytes: z.number(),
    /** True when a cap (pages or bytes) cut the collection short. */
    truncated: z.boolean(),
    pages: z.array(collectedPageSchema),
});
//# sourceMappingURL=research-ai-chain.js.map